<script setup>
import axios from 'axios'
import { ref, onMounted, computed, provide, watch } from 'vue'

import AssessmentHeader from '../AssessmentHeader/AssessmentHeader.vue'
import AssessmentNavigation from '../AssessmentNavigation/AssessmentNavigation.vue'
import AssessmentCategory from '../AssessmentCategory/AssessmentCategory.vue'
import AssessmentReview from '../AssessmentReview/AssessmentReview.vue'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner.vue'
import { useCatalogStructure } from '../../composables/useCatalogStructure'

axios.defaults.baseURL = window.location.origin + '/api/'

const catalog = ref(undefined)
const assessment = ref(undefined)
const currentStep = ref('welcome') // welcome, evaluation, rating, review
const currentCategoryId = ref(1031)
const globalVersion = ref(1)
const actionsCount = ref(undefined)
const menuExpanded = ref(false)
const review = ref(undefined)
const loadedReviewVersion = ref(undefined)
const initialLoadCompleted = ref(false)

const props = defineProps({
  uuid: {
    type: String,
    default: '',
  },
})

const categories = computed(() => {
  if (!catalogForCurrentStep.value?.areas) return []

  return catalogForCurrentStep.value.areas.flatMap((area) => area.categories)
})

const currentCategory = computed(() => {
  return categories.value.find(
    (category) => category.id == currentCategoryId.value,
  )
})

const currentCategoryIndex = computed(() =>
  categories.value.findIndex((c) => c.id == currentCategoryId.value),
)

const prevCategory = computed(() => {
  const idx = currentCategoryIndex.value
  return idx > 0 ? categories.value[idx - 1] : null
})

const nextCategory = computed(() => {
  const idx = currentCategoryIndex.value
  return idx < categories.value.length - 1 ? categories.value[idx + 1] : null
})

const currentAreaTitle = computed(() => {
  return catalog.value.areas.find(
    (area) => area.id == currentCategory.value.areaId,
  ).title
})

const evaluationCompleted = computed(() => {
  if (actionsCount.value === undefined) return false
  if (assessment.value === undefined) return false
  return actionsCount.value <= Object.keys(assessment.value?.evaluations).length
})

const ratingCompleted = computed(() => {
  return review.value !== undefined
})

const evaluations = computed(() => {
  if (assessment.value === undefined) return false
  return assessment.value.evaluations
})

const implementedActions = computed(() => {
  if (!catalog.value?.areas || !evaluations.value) return []
  const evals = evaluations.value
  const result = []
  for (const area of catalog.value.areas) {
    for (const category of area.categories) {
      for (const objective of category.objectives ?? []) {
        for (const action of objective.actions ?? []) {
          if (evals[action.id]?.value === 'implemented') {
            result.push({
              ...action,
              areaTitle: area.title,
              categoryTitle: category.title,
              objectiveTitle: objective.title,
            })
          }
        }
      }
    }
  }
  return result
})

const ratings = computed(() => {
  if (assessment.value === undefined) return false
  return assessment.value.ratings
})

const { catalogForCurrentStep } = useCatalogStructure(
  catalog,
  evaluations,
  ratings,
  currentStep,
)

const getCatalogData = function () {
  axios({
    method: 'get',
    url: axios.defaults.baseURL + 'catalog/' + props.uuid,
    responseType: 'json',
  }).then(function (response) {
    catalog.value = response.data.catalog
    actionsCount.value = response.data.actionsCount
  })
}

const getAssessmentData = function () {
  axios({
    method: 'get',
    url: axios.defaults.baseURL + 'assessment/' + props.uuid,
    responseType: 'json',
  }).then(function (response) {
    assessment.value = response.data
    globalVersion.value = response.data.meta.globalVersion
  })
}

const getReviewData = function () {
  axios({
    method: 'get',
    url:
      axios.defaults.baseURL +
      'assessment/' +
      props.uuid +
      '/evaluation-result',
    responseType: 'json',
  }).then(function (response) {
    review.value = response.data
    loadedReviewVersion.value = globalVersion.value
    initialLoadCompleted.value = true
  })
}

const getReviewStatus = function () {
  axios({
    method: 'get',
    url:
      axios.defaults.baseURL +
      'assessment/' +
      props.uuid +
      '/evaluation-result/status',
    responseType: 'json',
  })
    .then(function (response) {
      if (
        response?.data?.isComplete == true &&
        (loadedReviewVersion.value == undefined ||
          loadedReviewVersion.value < globalVersion.value)
      ) {
        getReviewData()
      } else {
        initialLoadCompleted.value = true
      }
    })
    .catch(function () {
      initialLoadCompleted.value = true
    })
}

const saveEvaluation = function (data) {
  axios({
    method: 'post',
    url:
      axios.defaults.baseURL +
      'assessment/' +
      props.uuid +
      '/evaluation/' +
      data.id,
    responseType: 'json',
    data: {
      value: data.value,
      customDefinition: data.customDefinition,
      version: data.version,
      globalVersion: globalVersion.value,
    },
  })
    .then(function (response) {
      assessment.value = response.data
      globalVersion.value = response.data.meta.globalVersion
      getReviewStatus()
    })
    .catch(function (error) {
      if (error.response) {
        if (error.response.status === 409) {
          alert(
            'Diese Maßnahme wurde zwischenzeitlich bereits neu beurteilt.\n\n' +
              'Nach Schließen des Dialogs wird die aktuelle Beurteilung geladen. ' +
              'Sie können diese dann ggf. im Anschluss korrigieren.',
          )

          getAssessmentData()
        } else {
          alert('Fehler beim Speichern.')
        }
      } else if (error.request) {
        // Request wurde gesendet, aber keine Antwort erhalten (Netzwerkfehler)
        console.error('Netzwerkfehler:', error.request)
        alert('Keine Verbindung zum Server. Bitte Internetverbindung prüfen.')
      } else {
        // Fehler beim Aufbau des Requests
        console.error('Request-Fehler:', error.message)
        alert('Ein unerwarteter Fehler ist aufgetreten.')
      }
    })
}

provide('onSaveEvaluation', saveEvaluation)

const saveRating = function (data) {
  let currentData = {}

  if (data.type == 'effort') {
    currentData = {
      globalVersion: globalVersion.value,
      type: data.type,
      effort: data.effort,
      effortVersion: data.effortVersion,
    }
  } else if (data.type == 'impact') {
    currentData = {
      globalVersion: globalVersion.value,
      type: data.type,
      impact: data.impact,
      impactVersion: data.impactVersion,
    }
  }

  axios({
    method: 'post',
    url:
      axios.defaults.baseURL +
      'assessment/' +
      props.uuid +
      '/rating/' +
      data.id,
    responseType: 'json',
    data: currentData,
  })
    .then(function (response) {
      assessment.value = response.data
      globalVersion.value = response.data.meta.globalVersion
      getReviewStatus()
    })
    .catch(function (error) {
      if (error.response) {
        if (error.response.status === 409) {
          alert(
            'Diese Maßnahme wurde zwischenzeitlich bereits neu bewertet.\n\n' +
              'Nach Schließen des Dialogs wird die aktuelle Bewertung geladen. ' +
              'Sie können diese dann ggf. im Anschluss korrigieren.',
          )

          getAssessmentData()
        } else {
          alert('Fehler beim Speichern.')
        }
      } else if (error.request) {
        // Request wurde gesendet, aber keine Antwort erhalten (Netzwerkfehler)
        console.error('Netzwerkfehler:', error.request)
        alert('Keine Verbindung zum Server. Bitte Internetverbindung prüfen.')
      } else {
        // Fehler beim Aufbau des Requests
        console.error('Request-Fehler:', error.message)
        alert('Ein unerwarteter Fehler ist aufgetreten.')
      }
    })
}

provide('onSaveRating', saveRating)

const selectCategoryById = function (event) {
  currentCategoryId.value = event.id
  document.getElementById('main-content')?.focus()
  if (menuExpanded.value) menuExpanded.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const toggleMenu = function () {
  if (menuExpanded.value) {
    menuExpanded.value = false
  } else {
    menuExpanded.value = true
  }
}

onMounted(() => {
  getCatalogData()
  getAssessmentData()
  getReviewStatus()
})

let initialRoutingDone = false

watch(
  [catalog, assessment, initialLoadCompleted],
  ([catalogVal, assessmentVal, loaded]) => {
    if (!catalogVal || !assessmentVal || !loaded || initialRoutingDone) return
    initialRoutingDone = true
    if (ratingCompleted.value) {
      currentStep.value = 'review'
    } else if (evaluationCompleted.value) {
      currentStep.value = 'rating'
    } else if (Object.keys(assessmentVal.evaluations).length > 0) {
      currentStep.value = 'evaluation'
    } else {
      currentStep.value = 'welcome'
    }
  },
)

watch(currentStep, () => {
  const firstCategory = catalogForCurrentStep.value?.areas?.[0]?.categories?.[0]

  if (firstCategory) {
    currentCategoryId.value = firstCategory.id

    // Optional: Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
})

// Initial: Erste Category setzen
onMounted(() => {
  const firstCategory = catalogForCurrentStep.value?.areas?.[0]?.categories?.[0]
  if (firstCategory && !currentCategoryId.value) {
    currentCategoryId.value = firstCategory.id
  }
})

const switchStep = (step) => {
  currentStep.value = step
  if (menuExpanded.value) menuExpanded.value = false
  document.getElementById('main-content')?.focus()
}
</script>

<template>
  <div
    class="Assessment"
    :class="{ 'Assessment--fullscreenContent': currentStep == 'review' }"
  >
    <LoadingSpinner
      v-if="
        catalog == undefined || assessment == undefined || !initialLoadCompleted
      "
    />

    <AssessmentHeader
      :menu-expanded="menuExpanded"
      :modified="assessment?.meta?.modified"
      :created="assessment?.meta?.created"
      @toggle-menu="toggleMenu"
    />

    <div class="Assessment-wrapper">
      <AssessmentNavigation
        v-if="assessment && catalog && currentStep != 'review'"
        class="Assessment-navigation fade-in"
        :current-step="currentStep"
        :current-category="currentCategory"
        :catalog="catalogForCurrentStep"
        :allow-rating="evaluationCompleted"
        :allow-review="ratingCompleted"
        :menu-expanded="menuExpanded"
        @open-step="switchStep($event.name)"
        @select-category-by-id="selectCategoryById($event)"
      />

      <main
        v-if="catalog && assessment"
        id="main"
        tabindex="-1"
        class="Assessment-main fade-in"
      >
        <div v-if="currentStep == 'welcome'" class="Assessment-welcome">
          <h1 v-if="catalog.headline" class="Assessment-welcomeHeadline">
            {{ catalog.headline }}
          </h1>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="Assessment-welcomeText" v-html="catalog.text" />
          <button
            class="Assessment-welcomeStart button button--primary"
            @click="switchStep('evaluation')"
          >
            <span>Selbsttest starten</span>
            <svg class="icon" aria-hidden="true">
              <use href="#arrow-right"></use>
            </svg>
          </button>
        </div>

        <AssessmentCategory
          v-if="currentStep == 'rating' || currentStep == 'evaluation'"
          :current-category="currentCategory"
          :current-area-title="currentAreaTitle"
          :current-step="currentStep"
          :assessment-data="assessment"
          :prev-category="prevCategory"
          :next-category="nextCategory"
          :evaluation-completed="evaluationCompleted"
          :rating-completed="ratingCompleted"
          @navigate-to-category="selectCategoryById($event)"
          @go-to-rating="switchStep('rating')"
          @go-to-review="switchStep('review')"
        />

        <AssessmentReview
          v-if="assessment && currentStep == 'review'"
          :review="review"
          :implemented-actions="implementedActions"
          @back="switchStep('rating')"
        />
      </main>
    </div>
  </div>
</template>
